import { useMemo, useState } from 'react';
import moment from 'moment';

import { FormControl, InputLabel, Select, TextField, makeStyles } from '@material-ui/core';

import { useFindOrders } from '../hooks/get-orders.hook';
import { OrderTableWithDetails } from '../components/Tables/OrderTableWithDetails.component';
import { ORDER_TABLE_HEADERS } from '../constants/order-table-headers.constant';
import { mapOrderToTableRow } from '../utils/map-order-to-table-row.util';
import { ORDER_STATUSES } from '../types/order-status.type';
import { ORDER_ADV_STATUSES } from '../types/order-adv-status.type';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
});

interface ActionContainerProps {
  statusFilter: string;
  advStatusFilter: string;
  deliveryDate: string;
  onStatusFilterChange: (value: string) => void;
  onAdvStatusFilterChange: (value: string) => void;
  onDeliveryDateChange: (value: string) => void;
}

function ActionContainer({
  statusFilter,
  advStatusFilter,
  deliveryDate,
  onStatusFilterChange,
  onAdvStatusFilterChange,
  onDeliveryDateChange,
}: ActionContainerProps) {
  const classes = useStyles();

  return (
    <section className={classes.filters}>
      <TextField
        size="small"
        label="Date de livraison"
        type="date"
        variant="outlined"
        value={deliveryDate}
        onChange={(e) => onDeliveryDateChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <FormControl variant="outlined" size="small">
        <InputLabel>Statut</InputLabel>
        <Select
          native
          label="Statut"
          value={statusFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onStatusFilterChange(e.target.value)}
        >
          <option value="all">Tous</option>
          <option value={ORDER_STATUSES.PAID}>Payée</option>
          <option value={ORDER_STATUSES.WAITING}>En attente</option>
          <option value={ORDER_STATUSES.PARTIAL}>Partielle</option>
          <option value={ORDER_STATUSES.CANCELED}>Annulée</option>
          <option value={ORDER_STATUSES.REFUND}>Remboursée</option>
          <option value={ORDER_STATUSES.REFUSE}>Refusée</option>
          <option value={ORDER_STATUSES.ABANDONED}>Abandonnée</option>
        </Select>
      </FormControl>
      <FormControl variant="outlined" size="small">
        <InputLabel>ADV</InputLabel>
        <Select
          native
          label="ADV"
          value={advStatusFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onAdvStatusFilterChange(e.target.value)}
        >
          <option value="all">Tous</option>
          <option value={ORDER_ADV_STATUSES.INPUT}>Saisie</option>
          <option value={ORDER_ADV_STATUSES.VALIDATE}>Validée</option>
          <option value={ORDER_ADV_STATUSES.PROCESSED}>Traitée</option>
          <option value={ORDER_ADV_STATUSES.ARCHIVED}>Archivée</option>
          <option value={ORDER_ADV_STATUSES.CANCELED}>Annulée</option>
        </Select>
      </FormControl>
    </section>
  );
}

export default function AdminOrdersListView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [advStatusFilter, setAdvStatusFilter] = useState<string>('all');
  const [deliveryDate, setDeliveryDate] = useState<string>(moment().format('YYYY-MM-DD'));

  const { loading, orders, totalCount } = useFindOrders({
    page: page + 1,
    limit: rowsPerPage,
    sortDirection: 'desc',
    filters: {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      advStatus: advStatusFilter !== 'all' ? advStatusFilter : undefined,
      deliveryDate: deliveryDate || undefined,
    },
  });

  const tableRows = useMemo(() => orders.map(mapOrderToTableRow), [orders]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleAdvStatusChange = (value: string) => {
    setAdvStatusFilter(value);
    setPage(0);
  };

  const handleDeliveryDateChange = (value: string) => {
    setDeliveryDate(value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Commandes</h2>
      <ActionContainer
        statusFilter={statusFilter}
        advStatusFilter={advStatusFilter}
        deliveryDate={deliveryDate}
        onStatusFilterChange={handleStatusChange}
        onAdvStatusFilterChange={handleAdvStatusChange}
        onDeliveryDateChange={handleDeliveryDateChange}
      />
      <OrderTableWithDetails
        maxHeight="75svh"
        headers={ORDER_TABLE_HEADERS}
        orders={tableRows}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={loading}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </section>
  );
}
